import { createContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthState, AuthProviderConfig } from "../types";
import { TOKEN_QUERY_PARAM } from "../constants";
import {
  setAccessToken,
  getAccessToken,
  clearAccessToken,
  isAuthenticated as checkIsAuthenticated,
} from "../token-storage";

export interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  config: AuthProviderConfig;
  children: ReactNode;
}

export function AuthProvider({ config, children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // 1. Check URL for token from auth redirect
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get(TOKEN_QUERY_PARAM);

    if (tokenFromUrl) {
      setAccessToken(tokenFromUrl);

      // Clean the token from the URL
      params.delete(TOKEN_QUERY_PARAM);
      const cleanSearch = params.toString();
      const cleanUrl =
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "") + window.location.hash;
      window.history.replaceState(null, "", cleanUrl);

      setState({
        accessToken: tokenFromUrl,
        isAuthenticated: true,
        isLoading: false,
      });
      return;
    }

    // 2. Fall back to existing cookie
    if (checkIsAuthenticated()) {
      setState({
        accessToken: getAccessToken(),
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = useMemo(() => {
    return () => {
      const signInPath = config.signInPath ?? "/sign-in";
      const returnTo = window.location.href;
      const url = `${config.authUrl}${signInPath}?redirect=${encodeURIComponent(returnTo)}`;
      window.location.href = url;
    };
  }, [config.authUrl, config.signInPath]);

  const logout = useMemo(() => {
    return () => {
      clearAccessToken();
      setState({
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
